<template>
  <div class="musubi-page musubi-page--cn">
    <header class="musubi-page__head">
      <h1>Canal Nav</h1>
      <p class="text-muted">
        Connect this workspace to Canal Nav for directory imports, engagement
        events and suppression sync. Tokens are encrypted at rest and never
        shown back in full.
      </p>
    </header>

    <div v-if="loading" class="text-muted">Loading…</div>
    <div v-else-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <template v-else>
      <MusubiSectionCard title="Canal Nav" icon="plug" class="cn-card-gap">
        <template #actions>
          <span v-if="configured" class="cn-status cn-status--ok"><CygnusIcon name="check-circle" class="me-1" />Connected</span>
          <span v-else class="cn-status cn-status--off">Not connected</span>
        </template>

        <div class="cn-field">
          <div class="cn-field__head">
            <span class="cn-field__label">Base URL</span>
            <span v-if="baseUrlSet" class="cn-field__hint">set</span>
          </div>
          <input
            v-model="form.cn_base_url"
            type="text"
            class="form-control form-control-sm"
            autocomplete="off"
            :placeholder="baseUrlDefault"
          >
          <p class="cn-field__help">The Canal Nav API root, e.g. https://api.canalnav.com</p>
          <p class="cn-field__help">Leave blank to use the platform default{{ baseUrlDefault ? ` (${baseUrlDefault})` : '' }}.</p>
        </div>

        <div v-for="f in secretFields" :key="f.key" class="cn-field">
          <div class="cn-field__head">
            <span class="cn-field__label">{{ f.label }}</span>
            <span v-if="f.set" class="cn-field__hint">set</span>
          </div>
          <input
            v-model="form[f.key]"
            type="password"
            class="form-control form-control-sm"
            autocomplete="new-password"
            :placeholder="f.hint || f.placeholder"
          >
          <p class="cn-field__help">{{ f.help }}</p>
          <p class="cn-field__help">
            Leave blank to keep the current token. Type <code>(clear)</code> + save to remove it.
          </p>
        </div>

        <p class="cn-field__help cn-inactive-note">
          The integration stays inactive until the service token is set.
        </p>

        <p v-if="drip && drip.installed" class="cn-field__help">
          Claim-invite drip: {{ drip.status }} ({{ drip.enrolled_active }} active enrolment{{ drip.enrolled_active === 1 ? '' : 's' }}).
        </p>
      </MusubiSectionCard>

      <div class="cn-save">
        <button type="button" class="btn btn-primary btn-sm" :disabled="saving" @click="save">
          <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="saving" />
          <span v-else>Save</span>
        </button>
        <div v-if="saveMessage" :class="['cn-save__msg', saveOk ? 'is-ok' : 'is-error']">{{ saveMessage }}</div>
      </div>
    </template>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

const ENDPOINT = '/api/v1/admin/integrations/canal-nav';

export default {
  name: 'AdminCanalNavIntegration',
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      configured: false,
      baseUrlDefault: '',
      baseUrlSet: false,
      drip: null,
      secretFields: [
        {
          key: 'cn_service_token',
          label: 'Service token',
          help: 'Sent to Canal Nav as X-Service-Token on imports/events/suppressions',
          placeholder: 'Not set',
          set: false,
          hint: '',
        },
        {
          key: 'cn_webhook_token',
          label: 'Webhook token',
          help: 'Expected from Canal Nav on the claimed webhook',
          placeholder: 'Not set',
          set: false,
          hint: '',
        },
      ],
      form: { cn_base_url: '', cn_service_token: '', cn_webhook_token: '' },
      loading: true,
      saving: false,
      error: null,
      saveMessage: '',
      saveOk: false,
    };
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const res = await this.api.get(ENDPOINT);
        const p = res?.data ?? res;
        this.configured = !!p.configured;
        this.baseUrlDefault = p.base_url_default || '';
        this.baseUrlSet = !!(p.base_url && p.base_url !== '');
        this.drip = p.claim_invite_drip || null;
        // Non-secret field primes with the tenant's own value; secrets stay
        // blank (the masked hint is only ever a placeholder, never a value).
        this.form.cn_base_url = p.base_url || '';
        this.form.cn_service_token = '';
        this.form.cn_webhook_token = '';
        const meta = { cn_service_token: p.service_token, cn_webhook_token: p.webhook_token };
        this.secretFields.forEach((f) => {
          const m = meta[f.key] || {};
          f.set = !!m.set;
          f.hint = m.hint || '';
        });
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Failed to load the Canal Nav configuration.';
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      this.saveMessage = '';
      this.saveOk = false;
      // Base URL always goes up (empty clears the override, falling back to the
      // platform default). Secrets go up only when the admin typed a new value
      // or the explicit '(clear)' sentinel - masked hints are never round-tripped.
      const body = { cn_base_url: this.form.cn_base_url ?? '' };
      ['cn_service_token', 'cn_webhook_token'].forEach((k) => {
        const v = this.form[k];
        if (v === '(clear)') body[k] = '(clear)';
        else if (v) body[k] = v;
      });
      try {
        const res = await this.api.put(ENDPOINT, body);
        const payload = res?.data ?? res;
        if (payload?.success) {
          this.saveOk = true;
          this.saveMessage = 'Saved.';
          await this.load();
        } else {
          this.saveMessage = payload?.error || 'Save failed.';
        }
      } catch (e) {
        this.saveMessage = e?.response?.data?.error || e?.message || 'Save failed.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.musubi-page--cn { padding: var(--m-space-6, 32px); max-width: 720px; }
.cn-card-gap { margin-top: 18px; }

.cn-status { font-size: 0.76rem; font-weight: 600; }
.cn-status--ok { color: var(--m-status-success, #10B981); }
.cn-status--off { color: var(--m-text-tertiary); }

.cn-field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 14px; }
.cn-field__head { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; }
.cn-field__label { font-size: 0.85rem; font-weight: 500; color: var(--m-text-primary); }
.cn-field__hint { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-tertiary); }
.cn-field__help { font-size: 0.76rem; color: var(--m-text-secondary); margin: 2px 0 0; }
.cn-inactive-note { margin-top: 10px; }

.cn-save { display: flex; align-items: center; gap: 10px; margin-top: 18px; }
.cn-save__msg { font-size: 0.85rem; }
.cn-save__msg.is-ok { color: var(--m-status-success, #10B981); }
.cn-save__msg.is-error { color: var(--m-status-danger, #EF4444); }
</style>
